import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const ContactPage = () => {
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [services, setServices] = useState([]);
  const [directories, setDirectories] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 1000 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setServices(allItems.filter(item => item.type === 'contact_service'));
        setDirectories(allItems.filter(item => item.type === 'contact_directory'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  // Render directory items
  const renderDirectoryItems = () => {
    return directories.map((item, index) => {
      const parts = item.description ? item.description.split('|') : ['', ''];
      const email = parts[0] || '';
      const description = parts[1] || '';
      
      return (
        <div className="predyx-directory-item" key={index}>
          <h5>{item.title}</h5>
          <a href={`mailto:${email}`}>{email}</a>
          <p>{description}</p>
        </div>
      );
    });
  };

  return (
    <div>
      <Helmet>
        <title>Contact Predyxiq </title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 91 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "40px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-2" style={{ lineHeight: "35px" }}>
            {parse(cms[91]?.cms_page_heading || "CONTACT <span>PREDYXIQ</span>")}
          </h2>
          <h4 style={{ fontSize: "18px", marginTop: "10px", fontWeight: "700", marginBottom: "15px" }}>
            {cms[91]?.cms_page_small_description || "Let's Shape the Future of Healthcare Together."}
          </h4>
          {parse(cms[91]?.cms_page_description || "")}
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW CAN WE HELP - CMS ID 92 + Items */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4 pr-5 pl-6">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[92]?.cms_page_heading || "How Can <span>We Help?</span>")}
            </h2>
          </div>
          <div className="mns row">
            {services.map((item, index) => (
              <div className="col-lg-6 col-md-6" key={index}>
                <div className="predyx-content-box">
                  <div className="info-card" style={{ padding: "0px" }}>
                    <h5>{item.title}</h5>
                    <p>{item.description}</p>
                    <a href="#" className="service-read-more">
                      {index === 0 ? 'Contact Our Clinical Team →' : 
                       index === 1 ? 'Speak with Our Enterprise Team →' :
                       index === 2 ? 'Explore Partnership Opportunities →' :
                       'Contact Communications →'}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* DIRECT COMMUNICATION CHANNELS - CMS ID 93 + Items */}
      {/* ============================================ */}
      <section className="predyx-contact-section py-5">
        <div className="container">
          <div className="predyx-contact-wrapper">
            <h2 className="predyx-contact-title text-center mb-4">
              {parse(cms[93]?.cms_page_heading || "Direct <span>Communication Channels</span>")}
            </h2>

            <div className="row no-gutters predyx-contact-box">
              {/* Left Side - Directory */}
              <div className="col-lg-6 predyx-directory-left">
                <h4 className="predyx-directory-title">Directory</h4>
                {renderDirectoryItems()}
              </div>

              {/* Right Side - Headquarters */}
              <div className="col-lg-6 predyx-headquarter-right">
                <div className="predyx-headquarter-box">
                  <h4 className="predyx-headquarter-title">
                    {cms[94]?.cms_page_heading || "📍 Headquarters"}
                  </h4>
                  {parse(cms[94]?.cms_page_description || "<p>Predyx Headquarters<br />New Delhi, India</p>")}

            
                  <div className="predyx-meeting-content">
                    <h5>{cms[95]?.cms_page_heading || "Schedule a Conversation"}</h5>
                    {parse(cms[95]?.cms_page_description || "<p>Sometimes the most impactful partnerships begin face-to-face. We welcome the opportunity to host you for a private discussion or arrange a digital consultation at your convenience.</p>")}
                    <a href="#" className="service-read-more">
                      Reserve a Time on Our Calendar →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 96 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "28px", color: "#fff", fontWeight: "600", marginBottom: "20px" }}>
              {parse(cms[96]?.cms_page_heading || "Every lasting partnership begins with a conversation.")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {cms[96]?.cms_page_small_description || "Whether you are guiding the health of a single patient, an entire enterprise, or an urban population, we welcome the opportunity to explore how Clinical Intelligence can support your mission."}
            </p>
            <a href="#" className="action-items-primary-btn1">
              We look forward to connecting
              <i className="fas fa-chevron-right" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;